import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import logo from "../assets/SamsoftixLogo.jpg";
import "./Home.css";

import db from "../firebase";

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

/* =========================================================
   SAMSOFTIX HOME PAGE
   Local SEO + International SEO + GEO
   ========================================================= */

const siteUrl = "https://www.samsoftix.com";

const logoUrl = logo.startsWith("http")
  ? logo
  : `${siteUrl}${logo.startsWith("/") ? logo : `/${logo}`}`;

/* =========================================================
   SERVICES
   ========================================================= */

const services = [
  {
    number: "01",
    title: "Website Development",
    text:
      "Professional business websites and custom web applications designed for performance, usability, search visibility and business growth.",
    points: [
      "Business and corporate websites",
      "React and Next.js development",
      "Responsive mobile-first design",
      "SEO-ready technical foundation",
    ],
    link: "/website_Service_Samsoftix",
    linkText: "Explore web development",
  },
  {
    number: "02",
    title: "Mobile App Development",
    text:
      "Modern Android and iOS applications built with Flutter and React Native for startups, businesses, schools and organizations.",
    points: [
      "Android and iOS applications",
      "Flutter and React Native",
      "Firebase and API integration",
      "Testing and publishing support",
    ],
    link: "/Mobile_App_Service_Samsoftix",
    linkText: "Explore mobile apps",
  },
  {
    number: "03",
    title: "AI & Intelligent Solutions",
    text:
      "Practical AI and machine-learning solutions that help businesses automate processes, analyze information and build smarter applications.",
    points: [
      "AI-powered application features",
      "Machine learning solutions",
      "Data analysis and automation",
      "AI model integration",
    ],
    link: "/AI_Solutions_Samsoftix",
    linkText: "Explore AI solutions",
  },
  {
    number: "04",
    title: "Ecommerce & Business Systems",
    text:
      "Online stores, booking systems, management portals and custom business software designed around everyday operations.",
    points: [
      "Shopify and WooCommerce",
      "Custom ecommerce platforms",
      "Booking and reservation systems",
      "Business management software",
    ],
    link: "#contact",
    linkText: "Discuss your project",
  },
  {
    number: "05",
    title: "Game Development",
    text:
      "Interactive 2D and 3D game experiences for mobile and web using technologies selected for the project.",
    points: [
      "2D and 3D games",
      "Educational games",
      "Casual mobile games",
      "Web-based experiences",
    ],
    link: "#contact",
    linkText: "Discuss a game idea",
  },
  {
    number: "06",
    title: "SEO, Hosting & Deployment",
    text:
      "Technical SEO foundations, website deployment, hosting support and performance improvements for a stronger online presence.",
    points: [
      "Technical SEO",
      "Hosting and domain setup",
      "Website deployment",
      "Performance improvements",
    ],
    link: "/Hosting",
    linkText: "View hosting solutions",
  },
];

/* =========================================================
   COURSES
   ========================================================= */

const courses = [
  {
    title: "Web Development Bootcamp",
    duration: "12 weeks · Part-time",
    text:
      "HTML, CSS, JavaScript, React, Node.js, databases, Git, deployment, SEO and practical projects.",
  },
  {
    title: "Mobile App Development",
    duration: "13 weeks",
    text:
      "Learn Flutter and React Native with practical app development, APIs, testing and publishing guidance.",
  },
  {
    title: "Game Development",
    duration: "16 weeks",
    text:
      "Learn 2D and 3D game development fundamentals using Unity and Phaser through practical projects.",
  },
  {
    title: "AI & Machine Learning",
    duration: "10 weeks",
    text:
      "Learn data workflows, machine learning fundamentals, model evaluation and practical AI application development.",
  },
  {
    title: "Ecommerce Development",
    duration: "6–14 weeks",
    text:
      "Learn Shopify, WooCommerce and custom ecommerce development with store structure, SEO and automation.",
  },
  {
    title: "Microsoft Office & Office Management",
    duration: "6–14 weeks",
    text:
      "Practical Word, Excel, PowerPoint, Outlook, reporting and office management skills.",
  },
];

/* =========================================================
   FAQ
   ========================================================= */

const faqs = [
  {
    question: "What services does SamSoftix provide?",
    answer:
      "SamSoftix provides website and web application development, mobile app development, ecommerce and business systems, game development, AI and machine learning solutions, SEO, hosting and technology training.",
  },
  {
    question: "Where is SamSoftix located?",
    answer:
      "SamSoftix is based in Wan, Sambrial, Punjab, Pakistan. The company serves businesses in Sambrial, Sialkot and across Pakistan, and provides remote development services to international clients.",
  },
  {
    question: "Does SamSoftix work with international clients?",
    answer:
      "Yes. SamSoftix can work remotely with businesses and organizations outside Pakistan. Projects can be discussed online, with requirements, milestones and communication arrangements agreed before development begins.",
  },
  {
    question: "What types of international projects can SamSoftix develop?",
    answer:
      "SamSoftix can discuss website development, mobile applications, ecommerce platforms, AI features, business systems and custom software projects with international clients.",
  },
  {
    question: "Does SamSoftix provide website development in Sambrial and Sialkot?",
    answer:
      "Yes. SamSoftix provides website development, software development, ecommerce, mobile applications and technology services for businesses in Sambrial, Sialkot and other areas of Pakistan.",
  },
  {
    question: "Does SamSoftix offer online training?",
    answer:
      "Yes. SamSoftix provides online and onsite technology training depending on the course and available batch.",
  },
  {
    question: "How can I contact SamSoftix?",
    answer:
      "You can contact SamSoftix through the website contact form, WhatsApp at +92 332 0472184 or email at info@samsoftix.com.",
  },
];

/* =========================================================
   COMPONENT
   ========================================================= */

function Home() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] =
    useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  /* =======================================================
   LOAD APPROVED & PUBLISHED REVIEWS
   ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadReviews = async () => {
      try {
        const reviewsQuery = query(
          collection(db, "reviews"),
          where("approved", "==", true),
          where("published", "==", true)
        );

        const snapshot = await getDocs(reviewsQuery);

        const approvedReviews = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const aTime =
              a.createdAt?.seconds ||
              a.timestamp?.seconds ||
              0;

            const bTime =
              b.createdAt?.seconds ||
              b.timestamp?.seconds ||
              0;

            return bTime - aTime;
          });

        if (mounted) {
          setReviews(approvedReviews);
        }
      } catch (error) {
        console.error(
          "Error loading approved reviews:",
          error
        );

        if (mounted) {
          setReviews([]);
        }
      } finally {
        if (mounted) {
          setReviewsLoading(false);
        }
      }
    };

    loadReviews();

    return () => {
      mounted = false;
    };
  }, []);
  /* =======================================================
   LOAD PORTFOLIO PROJECTS FROM FIRESTORE
   ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadProjects = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "projects")
        );

        const projectData = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((project) => project.status !== "hidden")
          .sort((a, b) => {
            const aTime =
              a.createdAt?.seconds ||
              a.timestamp?.seconds ||
              0;

            const bTime =
              b.createdAt?.seconds ||
              b.timestamp?.seconds ||
              0;

            return bTime - aTime;
          });

        if (mounted) {
          setProjects(projectData);
        }
      } catch (error) {
        console.error("Error loading projects:", error);
      } finally {
        if (mounted) {
          setProjectsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      mounted = false;
    };
  }, []);

  /* =======================================================
     AUTO REVIEW SLIDER
     ======================================================= */

  useEffect(() => {
    if (reviews.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setReviewIndex((current) => {
        return (current + 1) % reviews.length;
      });
    }, 5500);

    return () => clearInterval(interval);
  }, [reviews.length]);

  /* =======================================================
     CONTACT FORM
     ======================================================= */

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (
      !name.trim() ||
      !email.trim() ||
      !message.trim()
    ) {
      alert("Please fill in your name, email and message.");
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, "contacts"), {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
        type: "contact",
        createdAt: Timestamp.now(),
      });

      alert(
        "Message sent successfully. We will get back to you soon."
      );

      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (error) {
      console.error("Error sending contact message:", error);

      alert(
        "Unable to send your message right now. Please contact us on WhatsApp."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =======================================================
     NEWSLETTER FORM
     ======================================================= */

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    const cleanEmail = newsletterEmail.trim();

    if (!cleanEmail) {
      alert("Please enter your email address.");
      return;
    }

    setNewsletterSubmitting(true);

    try {
      await addDoc(collection(db, "newsletter"), {
        email: cleanEmail,
        type: "newsletter",
        createdAt: Timestamp.now(),
      });

      alert("You're subscribed. Thank you!");
      setNewsletterEmail("");
    } catch (error) {
      console.error("Error subscribing:", error);

      alert(
        "Unable to subscribe right now. Please try again."
      );
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  /* =======================================================
     STRUCTURED DATA
     ======================================================= */

  const structuredData = {
    "@context": "https://schema.org",

    "@graph": [
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `${siteUrl}/#business`,
        name: "SamSoftix",
        url: `${siteUrl}/`,
        logo: logoUrl,
        image: logoUrl,
        telephone: "+92-332-0472184",
        email: "info@samsoftix.com",

        description:
          "SamSoftix is a Pakistan-based software development company offering website development, mobile apps, AI solutions, ecommerce, custom software, game development, SEO, hosting and technology training to local and international clients.",

        address: {
          "@type": "PostalAddress",
          streetAddress: "Wan, Sambrial",
          addressLocality: "Wan",
          addressRegion: "Punjab",
          postalCode: "51010",
          addressCountry: "PK",
        },

        geo: {
          "@type": "GeoCoordinates",
          latitude: 32.4281447,
          longitude: 74.2979536,
        },

        areaServed: [
          {
            "@type": "City",
            name: "Wan",
          },
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "AdministrativeArea",
            name: "Punjab",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
          {
            "@type": "Country",
            name: "United States",
          },
          {
            "@type": "Country",
            name: "United Kingdom",
          },
          {
            "@type": "Country",
            name: "United Arab Emirates",
          },
          {
            "@type": "Country",
            name: "Saudi Arabia",
          },
          {
            "@type": "Country",
            name: "China",
          },
          {
            "@type": "Country",
            name: "Paris",
          },
          {
            "@type": "Country",
            name: "UK",
          },
          {
            "@type": "Country",
            name: "Australia",
          },
          {
            "@type": "Country",
            name: "Oman",
          },
          {
            "@type": "Country",
            name: "Kuwait",
          },
        ],

        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ],
            opens: "10:00",
            closes: "18:00",
          },
        ],

        sameAs: [
          "https://facebook.com/samsoftix",
          "https://linkedin.com/company/samsoftix",
          "https://instagram.com/samsoftix",
        ],

        knowsAbout: [
          "Website Development",
          "Web Application Development",
          "React Development",
          "Next.js Development",
          "Mobile App Development",
          "Flutter Development",
          "React Native Development",
          "AI Development",
          "Machine Learning",
          "Ecommerce Development",
          "Custom Software Development",
          "Game Development",
          "Technical SEO",
          "Technology Training",
        ],
      },

      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: "SamSoftix",
        url: `${siteUrl}/`,
        logo: logoUrl,
        email: "info@samsoftix.com",
        telephone: "+92-332-0472184",
        sameAs: [
          "https://facebook.com/samsoftix",
          "https://linkedin.com/company/samsoftix",
          "https://instagram.com/samsoftix",
        ],
      },

      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: `${siteUrl}/`,
        name: "SamSoftix",
        description:
          "Pakistan-based software development company providing web, mobile, AI, ecommerce and custom software services to local and international clients.",
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
        inLanguage: "en-PK",
      },

      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        url: `${siteUrl}/`,
        name:
          "SamSoftix | Software Development Company in Pakistan",
        description:
          "SamSoftix provides website development, mobile applications, AI, ecommerce and custom software for businesses in Pakistan and international markets.",
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#business`,
        },
        inLanguage: "en-PK",
      },

      {
        "@type": "ItemList",
        "@id": `${siteUrl}/#services`,
        name: "SamSoftix Software Development Services",
        itemListElement: services.map((service, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: service.title,
          url: service.link.startsWith("#")
            ? `${siteUrl}/${service.link}`
            : `${siteUrl}${service.link}`,
        })),
      },

      {
        "@type": "FAQPage",
        "@id": `${siteUrl}/#faq`,
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="home-page">
      <Helmet>
        <html lang="en-PK" />

        <title>
          SamSoftix | Software Development Company in Pakistan
        </title>

        <meta
          name="description"
          content="SamSoftix is a Pakistan-based software development company building websites, mobile apps, AI solutions, ecommerce platforms and custom software for local and international clients."
        />

        <meta
          name="keywords"
          content="SamSoftix, software development company, software house Pakistan, web development company, website development, mobile app development, Flutter development, React development, Next.js development, AI development, ecommerce development, custom software development, software company Sambrial, software company Sialkot"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta name="author" content="SamSoftix" />
        <meta name="theme-color" content="#07111f" />

        <meta
          name="geo.region"
          content="PK-PB"
        />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <meta
          name="geo.position"
          content="32.4281447;74.2979536"
        />

        <meta
          name="ICBM"
          content="32.4281447, 74.2979536"
        />

        <link
          rel="canonical"
          href={`${siteUrl}/`}
        />

        {/* Open Graph */}

        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="SamSoftix | Software Development Company"
        />

        <meta
          property="og:description"
          content="Web development, mobile apps, AI solutions, ecommerce and custom software for businesses in Pakistan and international markets."
        />

        <meta
          property="og:url"
          content={`${siteUrl}/`}
        />

        <meta
          property="og:image"
          content={logoUrl}
        />

        <meta
          property="og:image:alt"
          content="SamSoftix Software House"
        />

        <meta
          property="og:site_name"
          content="SamSoftix"
        />

        <meta
          property="og:locale"
          content="en_PK"
        />

        {/* Twitter */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="SamSoftix | Software Development Company"
        />

        <meta
          name="twitter:description"
          content="Websites, mobile apps, AI, ecommerce and custom software for local and international businesses."
        />

        <meta
          name="twitter:image"
          content={logoUrl}
        />

        <meta
          name="twitter:image:alt"
          content="SamSoftix Software House"
        />

        {/* Structured data */}

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
          Keep only one hero section.
      ===================================================== */}

      <header className="hero" id="top">
        <div
          className="hero-grid-lines"
          aria-hidden="true"
        />

        <div
          className="hero-glow hero-glow-one"
          aria-hidden="true"
        />

        <div
          className="hero-glow hero-glow-two"
          aria-hidden="true"
        />

        <div className="hero-inner">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              SOFTWARE DEVELOPMENT · DIGITAL SOLUTIONS
            </div>

            <h1>
              Build Digital Products
              <span> That Move Your Business Forward.</span>
            </h1>

            <p className="hero-lead">
              SamSoftix is a Pakistan-based software
              development company delivering websites,
              mobile applications, AI solutions, ecommerce
              platforms and custom software for businesses
              in Pakistan and international markets.
            </p>

            <div className="hero-actions">
              <a
                href="#contact"
                className="button button-primary"
              >
                Start a Project <span>→</span>
              </a>

              <a
                href="#services"
                className="button button-ghost"
              >
                Explore Services
              </a>
            </div>

            <div className="hero-highlights">
              <div className="hero-highlight">
                <span>✓</span>
                <div>
                  <strong>Business Focused</strong>
                  <small>
                    Solutions built around your goals
                  </small>
                </div>
              </div>

              <div className="hero-highlight">
                <span>✓</span>
                <div>
                  <strong>Modern Technology</strong>
                  <small>
                    React · Flutter · Firebase · AI
                  </small>
                </div>
              </div>

              <div className="hero-highlight">
                <span>✓</span>
                <div>
                  <strong>Local & International</strong>
                  <small>
                    Pakistan-based · Remote delivery
                  </small>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 35 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="hero-dashboard">
              <div className="dashboard-header">
                <div className="dashboard-brand">
                  <img
                    src={logo}
                    alt="SamSoftix software development company"
                    className="dashboard-logo"
                  />

                  <div>
                    <strong>SamSoftix</strong>
                    <span>Digital Solutions</span>
                  </div>
                </div>

                <div className="dashboard-status">
                  <span />
                  Available
                </div>
              </div>

              <div className="dashboard-main">
                <div className="dashboard-title">
                  <span>YOUR IDEA</span>
                  <strong>→</strong>
                  <span>DIGITAL PRODUCT</span>
                </div>

                <div className="dashboard-center">
                  <div className="dashboard-core">
                    <img src={logo} alt="" />
                    <strong>SamSoftix</strong>
                    <span>Software House</span>
                  </div>

                  <div className="dashboard-orbit orbit-one">
                    <span>WEB</span>
                  </div>

                  <div className="dashboard-orbit orbit-two">
                    <span>APP</span>
                  </div>

                  <div className="dashboard-orbit orbit-three">
                    <span>AI</span>
                  </div>

                  <div className="dashboard-orbit orbit-four">
                    <span>SHOP</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-footer">
                <div>
                  <small>Technology</small>
                  <strong>Modern Stack</strong>
                </div>

                <div>
                  <small>Delivery</small>
                  <strong>Local + Remote</strong>
                </div>

                <div>
                  <small>Focus</small>
                  <strong>Business Growth</strong>
                </div>
              </div>
            </div>

            <div className="floating-card floating-card-one">
              <span className="floating-icon">✦</span>
              <div>
                <strong>Modern Stack</strong>
                <small>React · Flutter · Firebase</small>
              </div>
            </div>

            <div className="floating-card floating-card-two">
              <span className="floating-check">✓</span>
              <div>
                <strong>Remote Collaboration</strong>
                <small>Pakistan and beyond</small>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="hero-bottom">
          <span>Web Development</span>
          <i />
          <span>Mobile Apps</span>
          <i />
          <span>AI Solutions</span>
          <i />
          <span>Ecommerce</span>
          <i />
          <span>Custom Software</span>
        </div>
      </header>

      <main>
        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="intro-strip">
          <div className="section-shell">
            <div className="intro-grid">
              <div>
                <span className="intro-number">01</span>
                <strong>Understand</strong>
                <p>
                  Your business, users and requirements.
                </p>
              </div>

              <div>
                <span className="intro-number">02</span>
                <strong>Build</strong>
                <p>
                  A practical and scalable digital solution.
                </p>
              </div>

              <div>
                <span className="intro-number">03</span>
                <strong>Launch</strong>
                <p>
                  With deployment and technical guidance.
                </p>
              </div>

              <div>
                <span className="intro-number">04</span>
                <strong>Grow</strong>
                <p>
                  With optimization and ongoing support.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            SERVICES
        ===================================================== */}

        <section
          className="services-section section-shell"
          id="services"
        >
          <div className="section-heading">
            <div>
              <div className="section-kicker">
                What we do
              </div>

              <h2><span>
                Technology solutions designed around
                real business needs.</span>
              </h2>
            </div>

            <p>
              From a professional business website to a
              complete custom software platform, SamSoftix
              helps businesses turn requirements into useful
              digital products.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.article
                className="service-card"
                key={service.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.05,
                }}
              >
                <div className="service-top">
                  <span className="service-number">
                    {service.number}
                  </span>

                  <span className="service-arrow">↗</span>
                </div>

                <h3>{service.title}</h3>
                <p>{service.text}</p>

                <ul>
                  {service.points.map((point) => (
                    <li key={point}>
                      <span>✓</span>
                      {point}
                    </li>
                  ))}
                </ul>

                {service.link.startsWith("#") ? (
                  <a
                    href={service.link}
                    className="text-link"
                  >
                    {service.linkText} →
                  </a>
                ) : (
                  <Link
                    to={service.link}
                    className="text-link"
                  >
                    {service.linkText} →
                  </Link>
                )}
              </motion.article>
            ))}
          </div>
        </section>

        {/* =====================================================
            PROCESS
        ===================================================== */}

        <section className="process-section section-shell">
          <div className="process-layout">
            <div className="process-intro">
              <div className="section-kicker">
                Our approach
              </div>

              <h2><span>
                A clear process from
                idea to launch.</span>
              </h2>

              <p>
                Good software starts with understanding the
                problem. We keep the development process
                practical and transparent.
              </p>

              <a
                href="#contact"
                className="button button-primary"
              >
                Discuss Your Idea →
              </a>
            </div>

            <div className="process-steps">
              <div className="process-step">
                <span>01</span>
                <div>
                  <h3>Discovery</h3>
                  <p>
                    We discuss requirements, users, business
                    goals, features and technical needs.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>02</span>
                <div>
                  <h3>Planning & Design</h3>
                  <p>
                    We structure the project, user experience
                    and technical architecture.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>03</span>
                <div>
                  <h3>Development</h3>
                  <p>
                    The product is developed in practical
                    stages with testing and progress review.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>04</span>
                <div>
                  <h3>Launch & Support</h3>
                  <p>
                    We help with deployment, publishing,
                    technical setup and post-launch guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            COURSES
        ===================================================== */}

        <section
          className="courses-section section-shell"
          id="courses"
        >
          <div className="section-heading">
            <div>
              <div className="section-kicker">
                Learn with SamSoftix
              </div>

              <h2><span>
                Practical technology training
                for real skills.</span>
              </h2>
            </div>

            <p>
              Structured courses covering web development,
              mobile apps, AI, ecommerce, games and office
              technology.
            </p>
          </div>

          <div className="courses-grid">
            {courses.map((course, index) => (
              <motion.article
                className="course-card"
                key={course.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.05,
                }}
              >
                <div className="course-index">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <div className="course-content">
                  <span className="course-duration">
                    {course.duration}
                  </span>

                  <h3>{course.title}</h3>
                  <p>{course.text}</p>

                  <a
                    href="#contact"
                    className="text-link"
                  >
                    Ask about this course →
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* =====================================================
            PORTFOLIO
        ===================================================== */}

        <section
          className="portfolio-section section-shell"
          id="portfolio"
        >
          <div className="section-heading">
            <div>
              <div className="section-kicker">
                Selected work
              </div>

              <h2>
                Projects across business, ecommerce and
                education.
              </h2>
            </div>

            <p>
              A selection of digital projects and experiences
              associated with SamSoftix.
            </p>
          </div>

          <div className="portfolio-grid">
            {projectsLoading ? (
              <div className="portfolio-empty">
                <p>Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="portfolio-empty">
                <p>Our latest projects will appear here.</p>
              </div>
            ) : (
              projects.map((project, index) => {
                const projectTitle =
                  project.title ||
                  project.name ||
                  "SamSoftix Project";

                const projectType =
                  project.type ||
                  project.category ||
                  project.service ||
                  "Digital Project";

                const projectText =
                  project.text ||
                  project.description ||
                  project.details ||
                  "";

                const projectUrl =
                  project.url ||
                  project.liveUrl ||
                  project.website ||
                  "";

                const projectImage =
                  project.image ||
                  project.imageUrl ||
                  project.thumbnail ||
                  "";

                const projectTechnologies =
                  Array.isArray(project.technologies)
                    ? project.technologies
                    : [];

                const projectContent = (
                  <>
                    {projectImage && (
                      <div className="portfolio-image">
                        <img
                          src={projectImage}
                          alt={projectTitle}
                          loading="lazy"
                        />
                      </div>
                    )}

                    <div className="portfolio-card-top">
                      <span>
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span>{projectType}</span>
                    </div>

                    <h3>{projectTitle}</h3>

                    {projectText && (
                      <p>{projectText}</p>
                    )}

                    {projectTechnologies.length > 0 && (
                      <div className="portfolio-technologies">
                        {projectTechnologies.map(
                          (technology, technologyIndex) => (
                            <span key={technologyIndex}>
                              {technology}
                            </span>
                          )
                        )}
                      </div>
                    )}

                    {projectUrl && (
                      <span className="portfolio-arrow">
                        View project ↗
                      </span>
                    )}
                  </>
                );

                if (projectUrl) {
                  return (
                    <a
                      className="portfolio-card"
                      href={projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={project.id || projectTitle}
                    >
                      {projectContent}
                    </a>
                  );
                }

                return (
                  <article
                    className="portfolio-card"
                    key={project.id || projectTitle}
                  >
                    {projectContent}
                  </article>
                );
              })
            )}
          </div>
        </section>

        {/* =====================================================
            REVIEWS
        ===================================================== */}

        <section
          className="reviews-section section-shell"
          id="reviews"
        >
          <div className="section-heading reviews-heading">
            <div>
              <div className="section-kicker">
                CLIENT FEEDBACK
              </div>

              <h2>
                What our clients and students
                <span> say about SamSoftix.</span>
              </h2>
            </div>

            <p>
              Feedback from clients, students and businesses
              who have worked with SamSoftix.
            </p>
          </div>

          <div className="reviews-carousel">
            {reviewsLoading ? (
              <div className="reviews-loading">
                <div className="review-loading-card" />
                <div className="review-loading-card" />
                <div className="review-loading-card" />
                <div className="review-loading-card" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="reviews-empty">
                <p>
                  Client feedback will appear here as reviews
                  are approved.
                </p>
              </div>
            ) : (
              <>
                <div className="reviews-track">
                  {Array.from(
                    {
                      length: Math.min(4, reviews.length),
                    },
                    (_, offset) =>
                      reviews[
                      (reviewIndex + offset) % reviews.length
                      ]
                  ).map((review, index) => {
                    const rating = Math.min(
                      5,
                      Math.max(0, Number(review.rating || 5))
                    );

                    const reviewName =
                      review.customerName ||
                      review.name ||
                      review.clientName ||
                      review.customer ||
                      "SamSoftix Client";

                    const reviewRole =
                      review.company ||
                      review.role ||
                      review.position ||
                      "Client";

                    const reviewMessage =
                      review.review ||
                      review.message ||
                      review.text ||
                      "";

                    return (
                      <motion.article
                        key={`${review.id || reviewName}-${reviewIndex}-${index}`}
                        className="review-card"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: index * 0.06,
                        }}
                      >
                        <div className="review-top">
                          <div className="review-person">
                            {review.image ? (
                              <img
                                src={review.image}
                                alt={`${reviewName}`}
                                loading="lazy"
                              />
                            ) : (
                              <div className="review-avatar">
                                {reviewName
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            )}

                            <div>
                              <h3>{reviewName}</h3>
                              <span>{reviewRole}</span>
                            </div>
                          </div>

                          <div
                            className="review-stars"
                            aria-label={`${rating} out of 5 stars`}
                          >
                            {Array.from({ length: 5 }).map(
                              (_, starIndex) => (
                                <span
                                  key={starIndex}
                                  className={
                                    starIndex < rating
                                      ? "filled"
                                      : "empty"
                                  }
                                >
                                  ★
                                </span>
                              )
                            )}
                          </div>
                        </div>

                        <div className="review-quote">“</div>

                        <p className="review-message">
                          {reviewMessage}
                        </p>

                        <div className="review-footer">
                          <span>
                            {review.service || "SamSoftix"}
                          </span>

                          <span>
                            {review.verified === true
                              ? "✓ Verified"
                              : "Client Feedback"}
                          </span>
                        </div>
                      </motion.article>
                    );
                  })}
                </div>

                {reviews.length > 1 && (
                  <div className="reviews-controls">
                    <button
                      type="button"
                      className="review-arrow"
                      onClick={() =>
                        setReviewIndex(
                          (reviewIndex - 1 + reviews.length) %
                          reviews.length
                        )
                      }
                      aria-label="Previous reviews"
                    >
                      ←
                    </button>

                    <div className="review-dots">
                      {reviews.map((review, index) => (
                        <button
                          type="button"
                          key={review.id || index}
                          className={
                            index === reviewIndex
                              ? "review-dot active"
                              : "review-dot"
                          }
                          onClick={() => setReviewIndex(index)}
                          aria-label={`Show review ${index + 1}`}
                        />
                      ))}
                    </div>

                    <button
                      type="button"
                      className="review-arrow"
                      onClick={() =>
                        setReviewIndex(
                          (reviewIndex + 1) % reviews.length
                        )
                      }
                      aria-label="Next reviews"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* =====================================================
            WHY SAMSOFTIX
        ===================================================== */}

        <section
          className="why-section section-shell"
          id="why-us"
        >
          <div className="why-grid">
            <div className="why-content">
              <div className="section-kicker">
                Why SamSoftix
              </div>

              <h2>
                Technology should solve problems,
                <span> not create more of them.</span>
              </h2>

              <p>
                We focus on useful digital solutions rather
                than unnecessary complexity. Whether you need
                a website, application, business system or
                training, our goal is to create something
                practical.
              </p>
            </div>

            <div className="why-list">
              <div>
                <span>✓</span>
                <div>
                  <h3>Business-focused development</h3>
                  <p>
                    Features are planned around users and
                    operational goals.
                  </p>
                </div>
              </div>

              <div>
                <span>✓</span>
                <div>
                  <h3>Local & remote collaboration</h3>
                  <p>
                    Work with us from Sambrial, Sialkot,
                    elsewhere in Pakistan or internationally.
                  </p>
                </div>
              </div>

              <div>
                <span>✓</span>
                <div>
                  <h3>Modern technology</h3>
                  <p>
                    React, Next.js, Flutter, React Native,
                    Firebase and other suitable technologies.
                  </p>
                </div>
              </div>

              <div>
                <span>✓</span>
                <div>
                  <h3>Support beyond development</h3>
                  <p>
                    Deployment, technical SEO foundations
                    and practical guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            LOCATION
        ===================================================== */}

        <section className="location-section section-shell">
          <div className="location-grid">
            <div className="location-content">
              <div className="section-kicker">
                Visit or contact us
              </div>

              <h2>SamSoftix Head Office</h2>

              <p className="location-address">
                <strong>
                  Wan, Sambrial, Punjab, Pakistan
                </strong>
              </p>

              <p>
                SamSoftix is based in Pakistan and provides
                software development and technology training
                locally. We also work remotely with clients
                in international markets.
              </p>

              <div className="contact-details">
                <a href="tel:+923320472184">
                  <span>Phone / WhatsApp</span>
                  +92 332 0472184
                </a>

                <a href="mailto:info@samsoftix.com">
                  <span>Email</span>
                  info@samsoftix.com
                </a>

                <div>
                  <span>Office hours</span>
                  Monday–Saturday · 10:00 AM–6:00 PM
                </div>
              </div>

              <div className="location-actions">
                <a
                  href="https://wa.me/923320472184"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button button-primary"
                >
                  Chat on WhatsApp
                </a>

                <a
                  href="#contact"
                  className="button button-ghost"
                >
                  Contact SamSoftix
                </a>
              </div>
            </div>

            <div className="map-wrap">
              <iframe
                title="SamSoftix Head Office - Wan, Sambrial, Punjab, Pakistan"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3367.6644686715035!2d74.2979536!3d32.4281447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391edf4d97cfdcad%3A0x11527b6087a2adbc!2sSamSoftix!5e0!3m2!1sen!2s!4v1769507979816!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>

        {/* =====================================================
            FAQ
        ===================================================== */}

        <section
          className="faq-section section-shell"
          id="faq"
        >
          <div className="section-heading centered">
            <div>
              <div className="section-kicker">
                Frequently Asked Questions
              </div>

              <h2>Questions about SamSoftix</h2>
            </div>
          </div>

          <div className="faq-grid">
            {faqs.map((faq) => (
              <details
                className="faq-item"
                key={faq.question}
              >
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* =====================================================
            CONTACT
        ===================================================== */}

        <section
          className="contact-section section-shell"
          id="contact"
        >
          <div className="contact-card">
            <div className="contact-copy">
              <div className="section-kicker">
                Start a conversation
              </div>

              <h2>
                Have a project, business idea or learning goal?
              </h2>

              <p>
                Tell us what you want to build or learn. Share
                your basic requirements and we'll help you
                understand the next step.
              </p>

              <div className="contact-mini-links">
                <a
                  href="https://wa.me/923320472184"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp · +92 332 0472184
                </a>

                <a href="mailto:info@samsoftix.com">
                  info@samsoftix.com
                </a>
              </div>
            </div>

            <form
              className="contact-form"
              onSubmit={handleContactSubmit}
            >
              <label>
                <span>Name *</span>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </label>

              <label>
                <span>Email *</span>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              <label>
                <span>Phone / WhatsApp</span>
                <input
                  type="tel"
                  placeholder="+92 3XX XXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                />
              </label>

              <label>
                <span>How can we help? *</span>
                <textarea
                  placeholder="Tell us about your project, business requirement or course enquiry..."
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </label>

              <button
                type="submit"
                className="button button-primary form-submit"
                disabled={submitting}
              >
                {submitting
                  ? "Sending..."
                  : "Send Project Enquiry →"}
              </button>
            </form>
          </div>
        </section>

        {/* =====================================================
            NEWSLETTER
        ===================================================== */}

        <section className="newsletter-section section-shell">
          <div className="newsletter-card">
            <div className="newsletter-copy">
              <div className="section-kicker">
                Stay updated
              </div>

              <h2>
                Get SamSoftix updates, course alerts and offers.
              </h2>

              <p>
                Occasional updates only. No unnecessary emails.
              </p>
            </div>

            <form
              className="newsletter-form"
              onSubmit={handleNewsletterSubmit}
            >
              <label
                className="sr-only"
                htmlFor="newsletter-email"
              >
                Email address
              </label>

              <div className="newsletter-input-wrap">
                <span
                  className="newsletter-input-icon"
                  aria-hidden="true"
                >
                  @
                </span>

                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) =>
                    setNewsletterEmail(e.target.value)
                  }
                  autoComplete="email"
                  required
                />
              </div>

              <button
                type="submit"
                className="button button-primary newsletter-button"
                disabled={newsletterSubmitting}
              >
                {newsletterSubmitting
                  ? "Joining..."
                  : "Subscribe"}
              </button>
            </form>
          </div>
        </section>
      </main>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <footer className="footer">
        <div className="footer-main">
          <div className="footer-brand">
            <img
              src={logo}
              alt="SamSoftix Software House"
              width="64"
              height="64"
              loading="lazy"
            />

            <div>
              <strong>SamSoftix</strong>
              <p>
                Software development & technology training.
              </p>
            </div>
          </div>

          <div className="footer-column">
            <h3>Services</h3>
            <a href="#services">Website Development</a>
            <a href="#services">Mobile App Development</a>
            <a href="#services">AI Solutions</a>
            <a href="#services">Ecommerce & Software</a>
            <a href="#services">SEO & Deployment</a>
          </div>

          <div className="footer-column">
            <h3>Company</h3>
            <a href="#portfolio">Projects</a>
            <a href="#courses">Training</a>
            <a href="#reviews">Reviews</a>
            <a href="#faq">FAQ</a>
            <a href="#contact">Contact</a>
          </div>

          <div className="footer-column">
            <h3>Contact</h3>

            <a
              href="https://wa.me/923320472184"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>

            <a href="mailto:info@samsoftix.com">
              info@samsoftix.com
            </a>

            <span>Wan, Sambrial, Punjab</span>
            <span>Pakistan</span>
          </div>
        </div>

        <div className="footer-bottom">
          <span>
            © {new Date().getFullYear()} SamSoftix. All rights
            reserved.
          </span>

          <span>
            Web development · Mobile apps · AI · Ecommerce ·
            Software · Training
          </span>
        </div>
      </footer>

      {/* =====================================================
          FLOATING WHATSAPP
      ===================================================== */}

      <a
        className="floating-whatsapp"
        href="https://wa.me/923320472184"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with SamSoftix on WhatsApp"
      >
        <span aria-hidden="true">📞</span>
        WhatsApp
      </a>
    </div>
  );
}

export default Home;